import React, { useEffect, useState } from 'react';
import {
  FaChevronDown, FaChevronLeft, FaChevronRight, FaChevronUp, FaEye, FaSort,
  FaTrash, FaFilter, FaPlus, FaClock, FaSearch, FaSyncAlt, FaUser,
  FaUserPlus, FaCalendarAlt, FaBook, FaEdit, FaInfoCircle, FaCheckCircle,
  FaPlay, FaExclamationTriangle, FaBan
} from 'react-icons/fa';
import axiosInstance from '../../../../axios';
import Modal from '../../../Other/Modal';

const formatDateToYYYYMMDD = (dateStr) => {
  if (!dateStr) return '';
  try {
    if (dateStr.includes('T')) {
      return dateStr.split('T')[0];
    }
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
      return dateStr;
    }
    const matches = dateStr.match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{4})$/);
    if (matches) {
      const day = matches[1].padStart(2, '0');
      const month = matches[2].padStart(2, '0');
      const year = matches[3];
      return `${year}-${month}-${day}`;
    }
    const d = new Date(dateStr);
    if (!isNaN(d.getTime())) {
      return d.toISOString().split('T')[0];
    }
  } catch (e) {
    console.error("Error formatting date:", e);
  }
  return '';
};

const formatTimeToHHMM = (timeStr) => {
  if (!timeStr) return '';
  try {
    const ampmMatch = timeStr.match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i);
    if (ampmMatch) {
      let hours = parseInt(ampmMatch[1], 10);
      const minutes = ampmMatch[2];
      const ampm = ampmMatch[3].toUpperCase();
      if (ampm === 'PM' && hours < 12) hours += 12;
      if (ampm === 'AM' && hours === 12) hours = 0;
      return `${String(hours).padStart(2, '0')}:${minutes}`;
    }
    const timeMatch = timeStr.match(/^(\d{2}):(\d{2})/);
    if (timeMatch) {
      return `${timeMatch[1]}:${timeMatch[2]}`;
    }
  } catch (e) {
    console.error("Error formatting time:", e);
  }
  return '';
};

const getAvatarBgColor = (name) => {
  const colors = [
    'bg-indigo-50 text-indigo-700 border-indigo-100/80',
    'bg-emerald-50 text-emerald-700 border-emerald-100/80',
    'bg-amber-50 text-amber-700 border-amber-100/80',
    'bg-rose-50 text-rose-700 border-rose-100/80',
    'bg-sky-50 text-sky-700 border-sky-100/80',
    'bg-violet-50 text-violet-700 border-violet-100/80',
    'bg-fuchsia-50 text-fuchsia-700 border-fuchsia-100/80'
  ];
  if (!name) return 'bg-slate-50 text-slate-705 text-slate-700 border-slate-100';
  let sum = 0;
  for (let i = 0; i < name.length; i++) {
    sum += name.charCodeAt(i);
  }
  return colors[sum % colors.length];
};

const GroupBatchList = ({ instructorId }) => {
  const [groups, setGroups] = useState([]);
  const [filteredGroups, setFilteredGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState('');
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [viewModal, setViewModal] = useState(false);

  // Syllabus session progress states
  const [sessionsList, setSessionsList] = useState([]);
  const [skipModalOpen, setSkipModalOpen] = useState(false);
  const [targetSession, setTargetSession] = useState(null);
  const [targetStatus, setTargetStatus] = useState('');
  const [skippedSessions, setSkippedSessions] = useState([]);
  const [skipReasons, setSkipReasons] = useState({});

  // Search and Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [batchFilter, setBatchFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [courseFilter, setCourseFilter] = useState('');
  const [availableBatches, setAvailableBatches] = useState([]);
  const [availableCourses, setAvailableCourses] = useState([]);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 8;

  // Sorting
  const [sortConfig, setSortConfig] = useState({ key: '', direction: 'asc' });

  // Filter panel visibility
  const [showFilters, setShowFilters] = useState(false);

  // Edit Batch States
  const [editModal, setEditModal] = useState(false);
  const [editingGroup, setEditingGroup] = useState(null);
  const [editStartDate, setEditStartDate] = useState('');
  const [editEndDate, setEditEndDate] = useState('');
  const [editStartTime, setEditStartTime] = useState('');
  const [editEndTime, setEditEndTime] = useState('');
  const [editIsWeekend, setEditIsWeekend] = useState(false);
  const [editInstructor, setEditInstructor] = useState('');
  const [allInstructors, setAllInstructors] = useState([]);
  const [editLoading, setEditLoading] = useState(false);
  const [editMessage, setEditMessage] = useState('');
  const [editConflictError, setEditConflictError] = useState(false);

  // Type Search / Autocomplete States for Add Member
  const [allStudents, setAllStudents] = useState([]);
  const [loadingStudents, setLoadingStudents] = useState(false);
  const [instructorSearchTerm, setInstructorSearchTerm] = useState('');
  const [selectedInstructorToAdd, setSelectedInstructorToAdd] = useState(null);
  const [instructorDropdownOpen, setInstructorDropdownOpen] = useState(false);
  const [studentSearchTerm, setStudentSearchTerm] = useState('');
  const [selectedStudentToAdd, setSelectedStudentToAdd] = useState(null);
  const [studentDropdownOpen, setStudentDropdownOpen] = useState(false);

  // Interactive View Modal Active Tab
  const [activeViewTab, setActiveViewTab] = useState('general'); // 'general' | 'instructors' | 'students'

  // Custom Modal search and list filters
  const [modalInstructorSearch, setModalInstructorSearch] = useState('');
  const [modalStudentSearch, setModalStudentSearch] = useState('');

  const getStatusBadge = (status) => {
    const normalized = (status || 'unknown').toLowerCase();
    switch (normalized) {
      case 'ongoing':
        return 'bg-emerald-50 text-emerald-700 ring-1 ring-inset ring-emerald-600/20';
      case 'upcoming':
        return 'bg-amber-50 text-amber-700 ring-1 ring-inset ring-amber-600/20';
      case 'completed':
        return 'bg-slate-50 text-slate-600 ring-1 ring-inset ring-slate-500/20';
      default:
        return 'bg-slate-50 text-slate-500 ring-1 ring-inset ring-slate-500/20';
    }
  };

  const fetchGroupsWithParams = async (status, batch, course) => {
    setLoading(true);
    setMessage('');
    try {
      const params = {};
      if (status !== 'all') params.status = status;
      if (batch !== 'all') params.batchName = batch;
      if (course) params.courseId = course;

      const response = await axiosInstance.get(`/admin/group-batch-list`, { params });

      if (response.data.error) {
        setMessage(response.data.message || 'Failed to fetch data.');
        setGroups([]);
        setFilteredGroups([]);
      } else {
        let data = response.data.data || [];
        if (instructorId) {
          data = data.filter(group =>
            Number(group.instructorId) === Number(instructorId) ||
            group.instructors?.some(inst => Number(inst.id) === Number(instructorId))
          );
        }
        // Sort by status: ongoing -> upcoming -> completed, then by groupId descending
        const statusOrder = {
          'ongoing': 1,
          'upcoming': 2,
          'completed': 3
        };
        const sortedData = [...data].sort((a, b) => {
          const statusA = (a.status || '').toLowerCase();
          const statusB = (b.status || '').toLowerCase();
          const orderA = statusOrder[statusA] || 99;
          const orderB = statusOrder[statusB] || 99;
          if (orderA !== orderB) {
            return orderA - orderB;
          }
          const idA = parseInt(a.groupId || 0, 10);
          const idB = parseInt(b.groupId || 0, 10);
          return idB - idA;
        });
        setGroups(sortedData);
        setFilteredGroups(sortedData);
      }
    } catch (error) {
      console.error(error);
      setMessage(error.response?.data?.message || 'Failed to load group batches. Please try again.');
      setGroups([]);
      setFilteredGroups([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchGroups = () => fetchGroupsWithParams(statusFilter, batchFilter, courseFilter);

  const fetchGroupDetail = async (groupId) => {
    try {
      const response = await axiosInstance.get(`/admin/group-batch/${groupId}`);
      if (!response.data.error) {
        setSelectedGroup(response.data.data);
        if (response.data.data && response.data.data.sessions) {
          setSessionsList(response.data.data.sessions);
        }
      } else {
        alert(response.data.message || 'Failed to fetch group details.');
      }
    } catch (error) {
      console.error("Error fetching group details:", error);
      alert(error.response?.data?.message || 'Failed to fetch group details.');
    }
  };

  const handleSessionInstructorChange = (courseDetailId, instId) => {
    setSessionsList(prev => prev.map(s => 
      s.courseDetailId === courseDetailId 
        ? { 
            ...s, 
            instructorId: instId ? Number(instId) : null, 
            instructorName: selectedGroup.instructors?.find(i => Number(i.id) === Number(instId))?.name || '' 
          } 
        : s
    ));
  };

  const handleSaveAssignments = async () => {
    try {
      const assignments = sessionsList.map(s => ({
        courseDetailId: s.courseDetailId,
        instructorId: s.instructorId
      }));
      const response = await axiosInstance.post(`/admin/group-batch/${selectedGroup.groupId}/assign-sessions`, {
        assignments
      });
      if (!response.data.error) {
        alert(response.data.message || "Assignments saved successfully!");
        fetchGroupDetail(selectedGroup.groupId);
      } else {
        alert(response.data.message || "Failed to save assignments.");
      }
    } catch (err) {
      console.error("Error saving assignments:", err);
      alert(err.response?.data?.message || "Failed to save assignments.");
    }
  };

  const executeStatusUpdate = async (updates) => {
    try {
      const response = await axiosInstance.post(`/admin/group-batch/${selectedGroup.groupId}/update-session-status`, {
        updates
      });
      if (!response.data.error) {
        alert(response.data.message || "Status updated successfully!");
        fetchGroupDetail(selectedGroup.groupId);
      } else {
        alert(response.data.message || "Failed to update status.");
      }
    } catch (err) {
      console.error("Error updating status:", err);
      alert(err.response?.data?.message || "Failed to update status.");
    }
  };

  const handleUpdateSessionStatusClick = (session, newStatus) => {
    const targetIndex = sessionsList.findIndex(s => s.courseDetailId === session.courseDetailId);
    if (targetIndex === -1) return;

    const precedingSkipped = [];
    for (let i = 0; i < targetIndex; i++) {
      const s = sessionsList[i];
      if (s.status === 'pending' || s.status === 'ongoing') {
        precedingSkipped.push(s);
      }
    }

    if (precedingSkipped.length > 0) {
      setTargetSession(session);
      setTargetStatus(newStatus);
      setSkippedSessions(precedingSkipped);
      const reasonsObj = {};
      precedingSkipped.forEach(s => {
        reasonsObj[s.courseDetailId] = '';
      });
      setSkipReasons(reasonsObj);
      setSkipModalOpen(true);
    } else {
      executeStatusUpdate([{
        courseDetailId: session.courseDetailId,
        status: newStatus,
        instructorId: session.instructorId
      }]);
    }
  };

  const handleSkipModalSubmit = async (e) => {
    e.preventDefault();
    for (const s of skippedSessions) {
      if (!skipReasons[s.courseDetailId] || !skipReasons[s.courseDetailId].trim()) {
        alert(`Please provide a skip reason for session: ${s.mainTitle || 'Session #' + s.courseDetailId}`);
        return;
      }
    }

    const updates = skippedSessions.map(s => ({
      courseDetailId: s.courseDetailId,
      status: 'skipped',
      skipReason: skipReasons[s.courseDetailId],
      instructorId: s.instructorId
    }));

    updates.push({
      courseDetailId: targetSession.courseDetailId,
      status: targetStatus,
      instructorId: targetSession.instructorId
    });

    await executeStatusUpdate(updates);
    setSkipModalOpen(false);
  };

  const fetchAvailableBatches = async () => {
    try {
      const response = await axiosInstance.get('/admin/available-batches');
      if (!response.data.error) {
        setAvailableBatches(response.data.data || []);
      }
    } catch (error) {
      console.error('Error fetching available batches:', error);
    }
  };

  const fetchAllInstructors = async () => {
    try {
      const res = await axiosInstance.post('/admin/instructors', {});
      setAllInstructors(res.data?.data || []);
    } catch (error) {
      console.error('Error fetching all instructors:', error);
    }
  };

  const fetchAllStudents = async () => {
    setLoadingStudents(true);
    try {
      const res = await axiosInstance.get("user/student", { params: { perPage: 1000 } });
      const data = res.data?.data?.data || res.data?.data || [];
      setAllStudents(data);
    } catch (error) {
      console.error("Error fetching all students:", error);
    } finally {
      setLoadingStudents(false);
    }
  };

  const fetchAvailableCourses = async () => {
    try {
      const uniqueCourses = [];
      const seen = new Set();
      groups.forEach(item => {
        if (item.courseId && !seen.has(item.courseId)) {
          seen.add(item.courseId);
          uniqueCourses.push({ id: item.courseId, name: item.courseName });
        }
      });
      setAvailableCourses(uniqueCourses);
    } catch (error) {
      console.error('Error fetching courses:', error);
    }
  };

  useEffect(() => {
    fetchGroups();
    fetchAvailableBatches();
    fetchAllInstructors();
    fetchAllStudents();
  }, [instructorId]);

  useEffect(() => {
    fetchAvailableCourses();
  }, [groups]);

  // Reset modal search fields when modal closes
  useEffect(() => {
    if (!viewModal) {
      setModalInstructorSearch('');
      setModalStudentSearch('');
      setInstructorSearchTerm('');
      setSelectedInstructorToAdd(null);
      setInstructorDropdownOpen(false);
      setStudentSearchTerm('');
      setSelectedStudentToAdd(null);
      setStudentDropdownOpen(false);
    }
  }, [viewModal]);

  // Apply search filter locally
  useEffect(() => {
    let filtered = [...groups];

    if (searchTerm) {
      filtered = filtered.filter(group =>
        group.courseName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        group.batchName.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    setFilteredGroups(filtered);
    setCurrentPage(1);
  }, [searchTerm, groups]);

  useEffect(() => {
    if (sortConfig.key !== '') {
      const sorted = [...filteredGroups].sort((a, b) => {
        let aVal = a[sortConfig.key];
        let bVal = b[sortConfig.key];

        if (sortConfig.key === 'instructors' || sortConfig.key === 'students') {
          aVal = a[sortConfig.key]?.length || 0;
          bVal = b[sortConfig.key]?.length || 0;
        }

        if (typeof aVal === 'string') {
          aVal = aVal.toLowerCase();
          bVal = bVal.toLowerCase();
        }

        if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
        if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
        return 0;
      });
      setFilteredGroups(sorted);
    }
  }, [sortConfig]);

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const handleDeleteBatch = async (groupId) => {
    if (window.confirm('Are you sure you want to delete this batch? This action cannot be undone.')) {
      try {
        const response = await axiosInstance.delete(`/admin/group-batch/${groupId}`);
        alert(response.data.message || 'Batch deleted successfully');
        fetchGroups();
      } catch (error) {
        console.error(error);
        alert(error.response?.data?.message || 'Failed to delete batch');
      }
    }
  };

  const handleAddInstructorSubmit = async (e) => {
    e.preventDefault();
    if (!selectedGroup || !selectedInstructorToAdd) {
      alert("Please search and select an instructor first.");
      return;
    }

    try {
      const res = await axiosInstance.post('/admin/group/add-member', {
        groupId: selectedGroup.groupId,
        userId: selectedInstructorToAdd.id,
        role: '4'
      });
      console.log("res:-------", res.data);
      alert(res.data.message);
      setSelectedInstructorToAdd(null);
      setInstructorSearchTerm('');
      fetchGroupDetail(selectedGroup.groupId);
      fetchGroups();
    } catch (err) {
      alert(err.response?.data?.message || "Failed to add instructor.");
    }
  };

  const handleAddStudentSubmit = async (e) => {
    e.preventDefault();
    if (!selectedGroup || !selectedStudentToAdd) {
      alert("Please search and select a student first.");
      return;
    }

    try {
      const res = await axiosInstance.post('/admin/group/add-member', {
        groupId: selectedGroup.groupId,
        userId: selectedStudentToAdd.id,
        role: '2'
      });
      console.log("res:------", res.data);
      alert(res.data.message);
      setSelectedStudentToAdd(null);
      setStudentSearchTerm('');
      fetchGroupDetail(selectedGroup.groupId);
      fetchGroups();
    } catch (err) {
      alert(err.response?.data?.message || "Failed to enroll student.");
    }
  };

  const handleRemoveMember = async (member) => {
    if (window.confirm(`Are you sure you want to remove ${member.name} from this group?`)) {
      try {
        const res = await axiosInstance.post('/admin/group/remove-member', {
          groupId: selectedGroup.groupId,
          userId: member.id,
          role: member.role,
        });
        alert(res.data.message);
        fetchGroupDetail(selectedGroup.groupId);
        fetchGroups();
      } catch (err) {
        console.error(err?.response?.data?.message || "Failed to remove member.");
        alert(err.response?.data?.message || "Failed to remove member.");
      }
    }
  };

  const handleEditClick = (group) => {
    setEditingGroup(group);
    setEditStartDate(formatDateToYYYYMMDD(group.startDate));
    setEditEndDate(formatDateToYYYYMMDD(group.endDate));
    setEditStartTime(formatTimeToHHMM(group.startTime));
    setEditEndTime(formatTimeToHHMM(group.endTime));
    setEditIsWeekend(group.daysType === 'Weekend');

    const instId = group.instructorId || (group.instructors && group.instructors[0]?.id) || '';
    setEditInstructor(instId);

    setEditMessage('');
    setEditConflictError(false);
    setEditModal(true);
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    if (!editStartDate || !editEndDate || !editStartTime || !editEndTime || !editInstructor) {
      alert('Please fill out all the fields.');
      return;
    }

    setEditLoading(true);
    setEditMessage('');
    setEditConflictError(false);

    try {
      const response = await axiosInstance.put(`/admin/group-batch/${editingGroup.groupId}`, {
        startDate: editStartDate,
        endDate: editEndDate,
        startTime: editStartTime,
        endTime: editEndTime,
        daysType: editIsWeekend ? 'Weekend' : 'Daily',
        instructorId: editInstructor
      });

      if (response.data.error) {
        setEditMessage(response.data.message || 'Failed to update batch.');
      } else {
        setEditMessage('Batch details successfully updated!');
        fetchGroups();
        setTimeout(() => {
          setEditModal(false);
          setEditingGroup(null);
        }, 1200);
      }
    } catch (error) {
      console.error('Error updating batch:', error);
      const errMsg = error.response?.data?.message || '';
      const isConflict = error.response?.data?.code === 'DUPLICATE_BATCH_TIMING' || errMsg.toLowerCase().includes('already exists') || errMsg.toLowerCase().includes('same instructor');

      if (isConflict) {
        setEditConflictError(true);
        setEditMessage(errMsg || 'A batch already exists for this course with the same instructor and timing. Please select a different instructor.');
      } else {
        setEditMessage(errMsg || 'Failed to update batch. Something went wrong.');
      }
    } finally {
      setEditLoading(false);
    }
  };

  const handleApplyFilters = () => {
    fetchGroupsWithParams(statusFilter, batchFilter, courseFilter);
  };

  const handleClearFilters = () => {
    setStatusFilter('all');
    setBatchFilter('all');
    setCourseFilter('');
    setSearchTerm('');
    fetchGroupsWithParams('all', 'all', '');
  };

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredGroups.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredGroups.length / itemsPerPage);

  // Autocomplete Filtering
  const filteredInstructorsToAdd = allInstructors.filter(inst => {
    const term = instructorSearchTerm.toLowerCase();
    const name = (inst.name || '').toLowerCase();
    const email = (inst.email || '').toLowerCase();
    return name.includes(term) || email.includes(term);
  });

  const filteredStudentsToAdd = allStudents.filter(std => {
    const term = studentSearchTerm.toLowerCase();
    const firstName = (std.firstName || '').toLowerCase();
    const lastName = (std.lastName || '').toLowerCase();
    const name = `${firstName} ${lastName}`.trim();
    const email = (std.email || '').toLowerCase();
    const username = (std.userName || '').toLowerCase();
    return name.includes(term) || email.includes(term) || username.includes(term);
  });

  return (
    <div className="space-y-4">
      {loading ? (
        <div className="flex flex-col items-center justify-center py-12 bg-white rounded-xl border border-slate-100 shadow-2xs">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-700 mb-3"></div>
          <p className="text-slate-500 font-semibold text-sm">Loading group batches...</p>
        </div>
      ) : (
        <>
          {message && (
            <div className={`p-4 rounded-xl border text-sm font-semibold transition-all shadow-2xs ${message.toLowerCase().includes('success')
              ? 'bg-emerald-50 text-emerald-800 border-emerald-100'
              : 'bg-rose-50 text-rose-800 border-rose-100'
              }`}>
              <div className="flex items-center gap-2.5">
                <span className={`w-2 h-2 rounded-full ${message.toLowerCase().includes('success') ? 'bg-emerald-500' : 'bg-rose-500'}`} />
                {message}
              </div>
            </div>
          )}

          {/* Action & Filter Header Card */}
          <div className="flex flex-col sm:flex-row justify-between items-stretch sm:items-center gap-4 bg-white p-4 rounded-xl border border-slate-300 shadow-2xs">
            <div className="relative flex-1 max-w-md">
              <span className="absolute inset-y-0 left-0 flex items-center pl-3.5 pointer-events-none text-slate-400">
                <FaSearch className="w-4 h-4" />
              </span>
              <input
                type="text"
                placeholder="Search course or batch..."
                className="w-full pl-10 pr-4 py-2.5 text-sm bg-white-50/40 border border-slate-400 rounded-lg placeholder:text-slate-400 text-slate-700 focus:outline-none focus:ring-2 focus:ring-slate-200 focus:bg-white transition-all font-medium"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>

            <div className="flex items-center gap-3 justify-end">
              <button
                className={`px-4 py-2.5 rounded-lg text-sm font-bold uppercase tracking-wider transition-all flex items-center gap-2 border shadow-2xs cursor-pointer ${showFilters
                  ? 'bg-slate-800 border-slate-800 text-white shadow-sm'
                  : 'bg-white hover:bg-slate-50 border-slate-200/60 text-slate-600'
                  }`}
                onClick={() => setShowFilters(!showFilters)}
              >
                <FaFilter className={`w-3.5 h-3.5 transition-colors ${showFilters ? 'text-white' : 'text-slate-400'}`} />
                Filters
              </button>

              <button
                className="bg-white hover:bg-slate-50 border border-slate-200/60 text-slate-600 px-4 py-2.5 rounded-lg text-sm font-bold uppercase tracking-wider transition-all flex items-center gap-2 shadow-2xs cursor-pointer"
                onClick={fetchGroups}
                title="Refresh list"
              >
                <FaSyncAlt className="w-3.5 h-3.5 text-slate-400 hover:text-slate-600 transition-colors animate-hover-spin" />
                Refresh
              </button>
            </div>
          </div>

          {showFilters && (
            <div className="bg-white border border-slate-100 p-5 rounded-xl shadow-2xs animate-fadeIn space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
                <div>
                  <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">Filter by Status</label>
                  <select
                    className="bg-white border border-slate-200/80 px-3 py-2 rounded-lg text-sm text-slate-700 w-full focus:outline-none focus:ring-2 focus:ring-slate-100 transition-all font-medium"
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                  >
                    <option value="all">All Status</option>
                    <option value="ongoing">Ongoing</option>
                    <option value="upcoming">Upcoming</option>
                    <option value="completed">Completed</option>
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">Filter by Batch</label>
                  <select
                    className="bg-white border border-slate-200/80 px-3 py-2 rounded-lg text-sm text-slate-700 w-full focus:outline-none focus:ring-2 focus:ring-slate-100 transition-all font-medium"
                    value={batchFilter}
                    onChange={(e) => setBatchFilter(e.target.value)}
                  >
                    <option value="all">All Batches</option>
                    {availableBatches.map((batch, index) => (
                      <option key={index} value={batch}>{batch}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">Filter by Course</label>
                  <select
                    className="bg-white border border-slate-200/80 px-3 py-2 rounded-lg text-sm text-slate-700 w-full focus:outline-none focus:ring-2 focus:ring-slate-100 transition-all font-medium"
                    value={courseFilter}
                    onChange={(e) => setCourseFilter(e.target.value)}
                  >
                    <option value="">All Courses</option>
                    {availableCourses.map((course, index) => (
                      <option key={index} value={course.id}>{course.name}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="flex gap-3 border-t border-slate-50 pt-4 justify-end">
                <button
                  className="bg-slate-50 hover:bg-slate-100 text-slate-600 font-bold text-xs uppercase tracking-wider py-2 px-4 rounded-lg transition-all cursor-pointer border border-slate-200/40"
                  onClick={handleClearFilters}
                >
                  Clear Filters
                </button>
                <button
                  className="bg-slate-800 hover:bg-slate-900 text-white font-bold text-xs uppercase tracking-wider py-2 px-5 rounded-lg transition-all shadow-2xs cursor-pointer"
                  onClick={handleApplyFilters}
                >
                  Apply Filters
                </button>
              </div>
            </div>
          )}

          {filteredGroups.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 bg-white rounded-xl border border-slate-100 shadow-2xs text-center">
              <span className="text-3xl text-slate-300 mb-2">🔍</span>
              <h4 className="text-slate-700 font-bold text-base">No Matching Groups</h4>
              <p className="text-slate-400 text-sm mt-1 max-w-xs font-medium">We couldn't find any group batches matching your criteria.</p>
              <button
                onClick={handleClearFilters}
                className="mt-4 text-xs font-bold uppercase tracking-wider bg-slate-50 hover:bg-slate-100 border border-slate-200 text-slate-600 px-4 py-2 rounded-lg transition-colors cursor-pointer"
              >
                Clear Filters
              </button>
            </div>
          ) : (
            <div className="bg-white border border-slate-300 rounded-xl shadow-2xs overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full border-collapse text-left">
                  <thead>
                    <tr className="bg-slate-50/50 border-b border-slate-300">
                      <th
                        className="cursor-pointer py-4 px-4 text-xs font-bold text-slate-500 uppercase tracking-wider select-none hover:bg-slate-100/50 transition-colors min-w-[180px]"
                        onClick={() => handleSort('courseName')}
                      >
                        <div className="flex items-center gap-1.5">
                          Course Name
                          {sortConfig.key === 'courseName' ? (
                            sortConfig.direction === 'asc' ? <FaChevronUp className="text-slate-600" /> : <FaChevronDown className="text-slate-600" />
                          ) : (
                            <FaSort className="text-slate-300 w-3 h-3" />
                          )}
                        </div>
                      </th>
                      <th
                        className="cursor-pointer py-4 px-4 text-xs font-bold text-slate-500 uppercase tracking-wider select-none hover:bg-slate-100/50 transition-colors min-w-[180px]"
                        onClick={() => handleSort('batchName')}
                      >
                        <div className="flex items-center gap-1.5">
                          Batch Name
                          {sortConfig.key === 'batchName' ? (
                            sortConfig.direction === 'asc' ? <FaChevronUp className="text-slate-600" /> : <FaChevronDown className="text-slate-600" />
                          ) : (
                            <FaSort className="text-slate-300 w-3 h-3" />
                          )}
                        </div>
                      </th>
                      <th className="py-4 px-4 text-xs font-bold text-slate-500 uppercase tracking-wider select-none min-w-[100px]">Status</th>
                      <th className="py-4 px-4 text-xs font-bold text-slate-500 uppercase tracking-wider select-none min-w-[160px]">Schedule</th>
                      <th
                        className="cursor-pointer py-4 px-4 text-xs font-bold text-slate-500 uppercase tracking-wider select-none hover:bg-slate-100/50 transition-colors min-w-[110px]"
                        onClick={() => handleSort('instructors')}
                      >
                        <div className="flex items-center gap-1.5">
                          Instructors
                          {sortConfig.key === 'instructors' ? (
                            sortConfig.direction === 'asc' ? <FaChevronUp className="text-slate-600" /> : <FaChevronDown className="text-slate-600" />
                          ) : (
                            <FaSort className="text-slate-300 w-3 h-3" />
                          )}
                        </div>
                      </th>
                      <th
                        className="cursor-pointer py-4 px-4 text-xs font-bold text-slate-500 uppercase tracking-wider select-none hover:bg-slate-100/50 transition-colors min-w-[100px]"
                        onClick={() => handleSort('students')}
                      >
                        <div className="flex items-center gap-1.5">
                          Students
                          {sortConfig.key === 'students' ? (
                            sortConfig.direction === 'asc' ? <FaChevronUp className="text-slate-600" /> : <FaChevronDown className="text-slate-600" />
                          ) : (
                            <FaSort className="text-slate-300 w-3 h-3" />
                          )}
                        </div>
                      </th>
                      <th className="py-4 px-4 text-xs font-bold text-slate-500 uppercase tracking-wider select-none min-w-[110px]">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100/80">
                    {currentItems.map((group, idx) => {
                      const status = group.status || 'unknown';
                      return (
                        <tr key={idx} className="hover:bg-slate-50/30 transition-colors border-b border-slate-200">
                          <td className="py-3.5 px-4 text-sm font-bold text-slate-800 max-w-[200px]">
                            <div className="flex items-center gap-2.5 min-w-0">
                              <div className="w-8 h-8 rounded bg-slate-50 border border-slate-100 flex items-center justify-center text-slate-500 flex-shrink-0">
                                <FaBook className="w-3.5 h-3.5 text-slate-400" />
                              </div>
                              <span className="truncate" title={group.courseName}>
                                {group.courseName}
                              </span>
                            </div>
                          </td>
                          <td className="py-3.5 px-4 text-sm text-slate-600 font-semibold max-w-[200px]">
                            <span className="block truncate" title={group.batchName}>{group.batchName}</span>
                          </td>
                          <td className="py-3.5 px-4">
                            <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wide ${getStatusBadge(status)}`}>
                              {status}
                            </span>
                          </td>
                          <td className="py-3.5 px-4">
                            <div className="flex flex-col gap-1">
                              {group.startTime ? (
                                <span className="text-sm font-bold text-slate-800">
                                  {group.startTime} - {group.endTime}
                                </span>
                              ) : (
                                <span className="text-xs text-slate-400 font-normal italic">Default (N/A)</span>
                              )}
                              <div className="flex items-center gap-1.5 text-xs text-slate-400 font-medium">
                                <span>{group.startDate || 'N/A'}</span>
                                <span className="text-slate-300">&bull;</span>
                                <span className="uppercase font-semibold">{group.daysType || 'Daily'}</span>
                              </div>
                            </div>
                          </td>
                          <td className="py-3.5 px-4">
                            <button
                              onClick={() => {
                                setSelectedGroup(group);
                                setActiveViewTab('instructors');
                                setViewModal(true);
                                fetchGroupDetail(group.groupId);
                              }}
                              className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold bg-indigo-50/60 text-indigo-700 hover:bg-indigo-100 hover:text-indigo-800 border border-indigo-100/50 transition-all cursor-pointer shadow-2xs"
                              title="Click to view all instructors"
                            >
                              <FaUser className="w-3.5 h-3.5 text-indigo-400" />
                              <span>{group.instructors?.length || 0}</span>
                            </button>
                          </td>
                          <td className="py-3.5 px-4">
                            <button
                              onClick={() => {
                                setSelectedGroup(group);
                                setActiveViewTab('students');
                                setViewModal(true);
                                fetchGroupDetail(group.groupId);
                              }}
                              className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold bg-sky-50/60 text-sky-700 hover:bg-sky-100 hover:text-sky-800 border border-sky-100/50 transition-all cursor-pointer shadow-2xs"
                              title="Click to view student list"
                            >
                              <FaUserPlus className="w-3.5 h-3.5 text-sky-400" />
                              <span>{group.students?.length || 0}</span>
                            </button>
                          </td>
                          <td className="py-3.5 px-4">
                            <div className="flex items-center gap-1.5">
                              <button
                                className="text-slate-400 hover:text-slate-800 hover:bg-slate-100 p-2 rounded-lg transition-all cursor-pointer"
                                onClick={() => {
                                  setSelectedGroup(group);
                                  setActiveViewTab('general');
                                  setViewModal(true);
                                  fetchGroupDetail(group.groupId);
                                }}
                                title="View Details"
                              >
                                <FaEye className="w-4 h-4" />
                              </button>
                              <button
                                className="text-slate-400 hover:text-slate-800 hover:bg-slate-100 p-2 rounded-lg transition-all cursor-pointer"
                                onClick={() => handleEditClick(group)}
                                title="Edit Batch"
                              >
                                <FaEdit className="w-4 h-4" />
                              </button>
                              {!instructorId && (
                                <button
                                  className="text-slate-300 hover:text-rose-600 hover:bg-rose-50 p-2 rounded-lg transition-all disabled:opacity-20 disabled:hover:bg-transparent cursor-pointer"
                                  onClick={() => handleDeleteBatch(group.groupId)}
                                  disabled={group.status !== 'completed'}
                                  title={group.status !== 'completed' ? 'Only completed batches can be deleted' : 'Delete batch'}
                                >
                                  <FaTrash className="w-4 h-4" />
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Compact Sleek Pagination Footer */}
              <div className="py-3 px-5 border-t border-slate-100/80 flex flex-col sm:flex-row items-center justify-between gap-4 bg-white">
                <div className="text-xs text-slate-400 font-bold uppercase tracking-wider">
                  Showing <span className="text-slate-700">{indexOfFirstItem + 1}</span> -{' '}
                  <span className="text-slate-700">
                    {Math.min(indexOfLastItem, filteredGroups.length)}
                  </span>{' '}
                  of <span className="text-slate-700">{filteredGroups.length}</span> batches
                </div>
                <div className="flex items-center space-x-1.5">
                  <button
                    onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                    disabled={currentPage === 1}
                    className="p-2 bg-white hover:bg-slate-50 border border-slate-100 rounded-lg text-slate-400 disabled:opacity-30 disabled:hover:bg-white transition-colors cursor-pointer"
                  >
                    <FaChevronLeft className="w-3.5 h-3.5" />
                  </button>
                  {[...Array(totalPages)].map((_, i) => (
                    <button
                      key={i}
                      onClick={() => setCurrentPage(i + 1)}
                      className={`w-8 h-8 flex items-center justify-center rounded-lg text-xs font-bold transition-all cursor-pointer ${currentPage === i + 1
                        ? 'bg-slate-800 text-white shadow-xs'
                        : 'border border-slate-100 text-slate-500 hover:bg-slate-50'
                        }`}
                    >
                      {i + 1}
                    </button>
                  ))}
                  <button
                    onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                    disabled={currentPage === totalPages}
                    className="p-2 bg-white hover:bg-slate-50 border border-slate-100 rounded-lg text-slate-400 disabled:opacity-30 disabled:hover:bg-white transition-colors cursor-pointer"
                  >
                    <FaChevronRight className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            </div>
          )}
        </>
      )}

      {/* Dynamic Segmented view modal */}
      <Modal isOpen={viewModal} onClose={() => setViewModal(false)} size="lg">
        {selectedGroup && (
          <div className="p-7 max-h-[85vh] overflow-y-auto space-y-6 bg-slate-50/30 rounded-2xl">
            {/* Modal Header */}
            <div className="bg-white border border-slate-100 p-6 rounded-2xl shadow-sm flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
              <div>
                <h3 className="text-2xl sm:text-3xl font-black text-slate-800 leading-tight tracking-tight">
                  {selectedGroup.courseName}
                </h3>
                <p className="text-sm text-slate-500 mt-2 font-bold uppercase tracking-wider flex items-center gap-2">
                  <span className="bg-slate-100 text-slate-700 px-2.5 py-1 rounded-md text-xs">Batch: {selectedGroup.batchName}</span>
                  <span className="text-slate-300">&bull;</span>
                  <span className="text-slate-400">ID: #{selectedGroup.groupId}</span>
                </p>
              </div>
              <span className={`inline-flex items-center px-4 py-1.5 rounded-full text-xs font-black uppercase tracking-wider ring-1 ring-inset ${getStatusBadge(selectedGroup.status)}`}>
                {selectedGroup.status}
              </span>
            </div>

            {/* Metrics Dashboard */}
            {(() => {
              const totalSessions = sessionsList?.length || 0;
              const completedSessions = sessionsList?.filter(s => s.status === 'completed').length || 0;
              const ongoingSessions = sessionsList?.filter(s => s.status === 'ongoing').length || 0;
              const skippedSessions = sessionsList?.filter(s => s.status === 'skipped').length || 0;
              const pendingSessions = sessionsList?.filter(s => s.status === 'pending').length || 0;
              const completionPercent = totalSessions > 0 ? Math.round((completedSessions / totalSessions) * 100) : 0;
              
              return (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 animate-fadeIn">
                  <div className="bg-white border border-slate-100 p-4 rounded-xl shadow-2xs hover:shadow-xs transition-shadow">
                    <span className="text-[10px] font-bold uppercase tracking-widest text-slate-400 block mb-1">Syllabus Progress</span>
                    <div className="flex items-baseline gap-1">
                      <span className="text-xl font-black text-slate-800">{completionPercent}%</span>
                      <span className="text-[10px] text-slate-500 font-bold">{completedSessions}/{totalSessions} Done</span>
                    </div>
                    <div className="w-full bg-slate-100 h-1.5 rounded-full mt-2 overflow-hidden flex">
                      <div style={{ width: `${completionPercent}%` }} className="bg-emerald-500 h-full rounded-full transition-all duration-500" />
                    </div>
                  </div>

                  <div className="bg-white border border-slate-100 p-4 rounded-xl shadow-2xs hover:shadow-xs transition-shadow">
                    <span className="text-[10px] font-bold uppercase tracking-widest text-slate-400 block mb-1">Enrolled Students</span>
                    <span className="text-xl font-black text-slate-800">{selectedGroup.students?.length || 0}</span>
                    <span className="text-[10px] text-slate-400 font-bold block mt-1">Enrolled & Active</span>
                  </div>

                  <div className="bg-white border border-slate-100 p-4 rounded-xl shadow-2xs hover:shadow-xs transition-shadow">
                    <span className="text-[10px] font-bold uppercase tracking-widest text-slate-400 block mb-1">Instructors</span>
                    <span className="text-xl font-black text-slate-800">{selectedGroup.instructors?.length || 0}</span>
                    <span className="text-[10px] text-slate-400 font-bold block mt-1">Leading Lectures</span>
                  </div>

                  <div className="bg-white border border-slate-100 p-4 rounded-xl shadow-2xs hover:shadow-xs transition-shadow">
                    <span className="text-[10px] font-bold uppercase tracking-widest text-slate-400 block mb-1">Active Sessions</span>
                    <div className="flex items-center gap-1.5">
                      <span className="text-xl font-black text-slate-800">{ongoingSessions}</span>
                      <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-blue-50 text-blue-700 ring-1 ring-blue-700/10">Active</span>
                    </div>
                    <span className="text-[10px] text-slate-400 font-semibold block mt-1">{pendingSessions} sessions queued</span>
                  </div>
                </div>
              );
            })()}

            {/* Tab Control */}
            <div className="flex flex-wrap bg-white border border-slate-100 p-1.5 rounded-xl shadow-3xs gap-1">
              <button
                onClick={() => setActiveViewTab('general')}
                className={`flex-1 min-w-[120px] flex items-center justify-center gap-2 py-2.5 text-sm font-extrabold uppercase tracking-wider rounded-lg transition-all cursor-pointer ${activeViewTab === 'general'
                  ? 'bg-slate-800 text-white shadow-xs'
                  : 'text-slate-400 hover:text-slate-700 hover:bg-slate-50'
                  }`}
              >
                <FaClock className="w-4 h-4" /> Schedule Info
              </button>
              <button
                onClick={() => setActiveViewTab('instructors')}
                className={`flex-1 min-w-[120px] flex items-center justify-center gap-2 py-2.5 text-sm font-extrabold uppercase tracking-wider rounded-lg transition-all cursor-pointer ${activeViewTab === 'instructors'
                  ? 'bg-slate-800 text-white shadow-xs'
                  : 'text-slate-400 hover:text-slate-700 hover:bg-slate-50'
                  }`}
              >
                <FaUser className="w-4 h-4" /> Instructors ({selectedGroup.instructors?.length || 0})
              </button>
              <button
                onClick={() => setActiveViewTab('students')}
                className={`flex-1 min-w-[120px] flex items-center justify-center gap-2 py-2.5 text-sm font-extrabold uppercase tracking-wider rounded-lg transition-all cursor-pointer ${activeViewTab === 'students'
                  ? 'bg-slate-800 text-white shadow-xs'
                  : 'text-slate-400 hover:text-slate-700 hover:bg-slate-50'
                  }`}
              >
                <FaUserPlus className="w-4 h-4" /> Students ({selectedGroup.students?.length || 0})
              </button>
              <button
                onClick={() => setActiveViewTab('sessions')}
                className={`flex-1 min-w-[120px] flex items-center justify-center gap-2 py-2.5 text-sm font-extrabold uppercase tracking-wider rounded-lg transition-all cursor-pointer ${activeViewTab === 'sessions'
                  ? 'bg-slate-800 text-white shadow-xs'
                  : 'text-slate-400 hover:text-slate-700 hover:bg-slate-50'
                  }`}
              >
                <FaBook className="w-4 h-4" /> Syllabus Progress
              </button>
            </div>

            {/* General schedule info */}
            {activeViewTab === 'general' && (
              <div className="space-y-4 animate-fadeIn">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 bg-white p-6 rounded-2xl border border-slate-100 shadow-2xs">
                  <div className="flex items-start gap-4 p-4 rounded-xl bg-slate-50 border border-slate-100/50">
                    <div className="w-12 h-12 rounded-xl bg-indigo-50 border border-indigo-100 flex items-center justify-center text-indigo-600 flex-shrink-0">
                      <FaClock className="w-6 h-6" />
                    </div>
                    <div>
                      <span className="font-bold text-slate-400 block text-xs uppercase tracking-widest mb-1">Class Timing</span>
                      <span className="text-slate-800 text-lg font-black">{selectedGroup.startTime ? `${selectedGroup.startTime} - ${selectedGroup.endTime}` : 'Default (N/A)'}</span>
                    </div>
                  </div>
                  <div className="flex items-start gap-4 p-4 rounded-xl bg-slate-50 border border-slate-100/50">
                    <div className="w-12 h-12 rounded-xl bg-emerald-50 border border-emerald-100 flex items-center justify-center text-emerald-600 flex-shrink-0">
                      <FaCalendarAlt className="w-6 h-6" />
                    </div>
                    <div>
                      <span className="font-bold text-slate-400 block text-xs uppercase tracking-widest mb-1">Date Range</span>
                      <span className="text-slate-800 text-base font-black leading-snug">{selectedGroup.startDate ? `${selectedGroup.startDate} to ${selectedGroup.endDate}` : 'N/A'}</span>
                    </div>
                  </div>
                  <div className="flex items-start gap-4 p-4 rounded-xl bg-slate-50 border border-slate-100/50 col-span-1 sm:col-span-2">
                    <div className="w-12 h-12 rounded-xl bg-amber-50 border border-amber-100 flex items-center justify-center text-amber-600 flex-shrink-0">
                      <FaBook className="w-6 h-6" />
                    </div>
                    <div>
                      <span className="font-bold text-slate-400 block text-xs uppercase tracking-widest mb-1">Schedule Type</span>
                      <span className="inline-flex items-center text-slate-800 text-sm font-black uppercase tracking-wider bg-amber-100/70 border border-amber-200/50 px-3 py-1 rounded-md">{selectedGroup.daysType || 'Daily'}</span>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Instructors List and Autocomplete Search-based Assigner */}
            {activeViewTab === 'instructors' && (
              <div className="space-y-6 animate-fadeIn">
                <div className="bg-slate-100/70 p-4 rounded-xl border border-slate-200/30 text-sm text-slate-650 font-semibold leading-relaxed">
                  Showing all assigned instructors for course <strong className="text-slate-800 font-extrabold">{selectedGroup.courseName}</strong>. Search and select an instructor to assign.
                </div>

                <form onSubmit={handleAddInstructorSubmit} className="bg-white border border-slate-150 p-5 rounded-2xl shadow-xs flex flex-col sm:flex-row items-stretch sm:items-end gap-4 relative">
                  <div className="flex-1 relative">
                    <label className="block text-sm font-extrabold text-slate-600 uppercase tracking-wider mb-2">Search & Assign Instructor</label>
                    <div className="relative">
                      <input
                        type="text"
                        placeholder="Type name or email to search..."
                        value={instructorSearchTerm}
                        onChange={(e) => {
                          setInstructorSearchTerm(e.target.value);
                          if (!selectedInstructorToAdd) {
                            setInstructorDropdownOpen(true);
                          } else if (selectedInstructorToAdd && e.target.value !== selectedInstructorToAdd.name) {
                            setSelectedInstructorToAdd(null);
                            setInstructorDropdownOpen(true);
                          }
                        }}
                        onFocus={() => setInstructorDropdownOpen(true)}
                        onBlur={() => {
                          setTimeout(() => setInstructorDropdownOpen(false), 200);
                        }}
                        className="w-full bg-slate-50 hover:bg-slate-100/50 border border-slate-200 px-4 py-3 pr-10 rounded-xl text-base text-slate-700 font-semibold focus:outline-none focus:ring-2 focus:ring-slate-800 transition-all h-[50px]"
                        required
                      />
                      {instructorSearchTerm && (
                        <button
                          type="button"
                          onClick={() => {
                            setSelectedInstructorToAdd(null);
                            setInstructorSearchTerm('');
                          }}
                          className="absolute right-3.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 cursor-pointer text-sm font-bold"
                        >
                          ✕
                        </button>
                      )}
                    </div>
                    {instructorDropdownOpen && filteredInstructorsToAdd.length > 0 && (
                      <div className="absolute left-0 right-0 mt-1.5 max-h-56 overflow-y-auto bg-white border border-slate-200 rounded-xl shadow-lg z-50 divide-y divide-slate-100">
                        {filteredInstructorsToAdd.map((inst) => (
                          <div
                            key={inst.id}
                            onClick={() => {
                              setSelectedInstructorToAdd(inst);
                              setInstructorSearchTerm(inst.name);
                              setInstructorDropdownOpen(false);
                            }}
                            className="px-4 py-3 hover:bg-slate-50 cursor-pointer text-sm font-semibold transition-colors flex flex-col"
                          >
                            <span className="text-slate-800">{inst.name}</span>
                            <span className="text-xs text-slate-400 font-medium">{inst.email}</span>
                          </div>
                        ))}
                      </div>
                    )}
                    {instructorDropdownOpen && instructorSearchTerm && filteredInstructorsToAdd.length === 0 && (
                      <div className="absolute left-0 right-0 mt-1.5 p-4 bg-white border border-slate-200 rounded-xl shadow-lg z-50 text-center text-xs text-slate-400 font-semibold">
                        No instructors found matching "{instructorSearchTerm}"
                      </div>
                    )}
                  </div>
                  <button
                    type="submit"
                    className="bg-slate-800 hover:bg-slate-900 text-white font-extrabold text-sm uppercase tracking-wider px-6 py-3 rounded-xl shadow-xs transition-all cursor-pointer flex items-center justify-center gap-2 h-[50px]"
                  >
                    <FaPlus className="w-4 h-4" /> Assign
                  </button>
                </form>

                <div className="space-y-3">
                  <div className="flex flex-col sm:flex-row justify-between items-stretch sm:items-center gap-2">
                    <h4 className="text-xs font-black text-slate-400 uppercase tracking-widest">Currently Assigned</h4>
                    <div className="relative w-full sm:w-64">
                      <span className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none text-slate-400">
                        <FaSearch className="w-3 h-3" />
                      </span>
                      <input
                        type="text"
                        placeholder="Filter list..."
                        value={modalInstructorSearch}
                        onChange={(e) => setModalInstructorSearch(e.target.value)}
                        className="w-full pl-8 pr-3 py-1.5 text-xs bg-white border border-slate-200 rounded-lg placeholder:text-slate-400 text-slate-705 focus:outline-none focus:ring-1 focus:ring-slate-400 font-semibold"
                      />
                    </div>
                  </div>

                  {(() => {
                    const displayInstructors = (selectedGroup.instructors || []).filter(inst => {
                      if (!modalInstructorSearch) return true;
                      const term = modalInstructorSearch.toLowerCase();
                      const name = (inst.name || '').toLowerCase();
                      const phone = (inst.phoneNumber || '').toLowerCase();
                      const email = (inst.email || '').toLowerCase();
                      return name.includes(term) || phone.includes(term) || email.includes(term);
                    });

                    return displayInstructors.length > 0 ? (
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        {displayInstructors.map((inst) => {
                          const initials = inst?.name ? inst.name.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase() : 'IN';
                          const avatarStyle = getAvatarBgColor(inst?.name);
                          return (
                            <div key={inst?.id} className="flex justify-between items-center bg-white border border-slate-100 p-4 rounded-2xl transition-all hover:shadow-xs">
                              <div className="flex items-center gap-4">
                                <div className={`w-11 h-11 rounded-full flex items-center justify-center text-sm font-extrabold border flex-shrink-0 shadow-3xs ${avatarStyle}`}>
                                  {initials}
                                </div>
                                <div>
                                  <p className="font-extrabold text-slate-800 text-base leading-tight">{inst?.name}</p>
                                  <p className="text-xs text-slate-400 font-semibold mt-1.5">{inst?.phoneNumber || 'No phone listed'}</p>
                                </div>
                              </div>
                              <button
                                onClick={() => handleRemoveMember(inst)}
                                className="text-slate-350 hover:text-rose-600 hover:bg-rose-50 p-2.5 rounded-xl transition-all cursor-pointer"
                                title="Remove Instructor"
                              >
                                <FaTrash className="w-4 h-4" />
                              </button>
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <p className="text-sm text-slate-405 italic font-semibold bg-white p-5 rounded-2xl text-center border border-slate-100 shadow-2xs">
                        {modalInstructorSearch ? 'No matching instructors found.' : 'No instructors assigned to this batch group.'}
                      </p>
                    );
                  })()}
                </div>
              </div>
            )}

            {/* Enrolled Students List and Search-based Enrollment Form */}
            {activeViewTab === 'students' && (
              <div className="space-y-6 animate-fadeIn">
                <div className="bg-slate-100/70 p-4 rounded-xl border border-slate-200/30 text-sm text-slate-650 font-semibold leading-relaxed">
                  List of students currently enrolled in <strong className="text-slate-800 font-extrabold">{selectedGroup.courseName} ({selectedGroup.batchName})</strong>. Search and select a student to enroll them.
                </div>

                <form onSubmit={handleAddStudentSubmit} className="bg-white border border-slate-155 p-5 rounded-2xl shadow-xs flex flex-col sm:flex-row items-stretch sm:items-end gap-4 relative">
                  <div className="flex-1 relative">
                    <label className="block text-sm font-extrabold text-slate-600 uppercase tracking-wider mb-2">Search & Enroll Student</label>
                    <div className="relative">
                      <input
                        type="text"
                        placeholder="Type name, email, or username to search..."
                        value={studentSearchTerm}
                        onChange={(e) => {
                          setStudentSearchTerm(e.target.value);
                          if (!selectedStudentToAdd) {
                            setStudentDropdownOpen(true);
                          } else if (selectedStudentToAdd && e.target.value !== `${selectedStudentToAdd.firstName || ''} ${selectedStudentToAdd.lastName || ''}`.trim()) {
                            setSelectedStudentToAdd(null);
                            setStudentDropdownOpen(true);
                          }
                        }}
                        onFocus={() => setStudentDropdownOpen(true)}
                        onBlur={() => {
                          setTimeout(() => setStudentDropdownOpen(false), 200);
                        }}
                        className="w-full bg-slate-50 hover:bg-slate-100/50 border border-slate-200 px-4 py-3 pr-10 rounded-xl text-base text-slate-700 font-semibold focus:outline-none focus:ring-2 focus:ring-slate-800 transition-all h-[50px]"
                        required
                      />
                      {studentSearchTerm && (
                        <button
                          type="button"
                          onClick={() => {
                            setSelectedStudentToAdd(null);
                            setStudentSearchTerm('');
                          }}
                          className="absolute right-3.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 cursor-pointer text-sm font-bold"
                        >
                          ✕
                        </button>
                      )}
                    </div>
                    {studentDropdownOpen && filteredStudentsToAdd.length > 0 && (
                      <div className="absolute left-0 right-0 mt-1.5 max-h-56 overflow-y-auto bg-white border border-slate-200 rounded-xl shadow-lg z-50 divide-y divide-slate-100">
                        {filteredStudentsToAdd.map((std) => {
                          const fullName = `${std.firstName || ''} ${std.lastName || ''}`.trim();
                          return (
                            <div
                              key={std.id}
                              onClick={() => {
                                setSelectedStudentToAdd(std);
                                setStudentSearchTerm(fullName);
                                setStudentDropdownOpen(false);
                              }}
                              className="px-4 py-3 hover:bg-slate-50 cursor-pointer text-sm font-semibold transition-colors flex flex-col"
                            >
                              <span className="text-slate-800">{fullName}</span>
                              <span className="text-xs text-slate-400 font-medium">{std.email || std.userName}</span>
                            </div>
                          );
                        })}
                      </div>
                    )}
                    {studentDropdownOpen && studentSearchTerm && filteredStudentsToAdd.length === 0 && (
                      <div className="absolute left-0 right-0 mt-1.5 p-4 bg-white border border-slate-200 rounded-xl shadow-lg z-50 text-center text-xs text-slate-400 font-semibold">
                        No students found matching "{studentSearchTerm}"
                      </div>
                    )}
                  </div>
                  <button
                    type="submit"
                    className="bg-slate-800 hover:bg-slate-900 text-white font-extrabold text-sm uppercase tracking-wider px-6 py-3.5 rounded-xl shadow-xs transition-all cursor-pointer flex items-center justify-center gap-2 h-[50px]"
                  >
                    <FaPlus className="w-4 h-4" /> Enroll
                  </button>
                </form>

                <div className="space-y-3">
                  <div className="flex flex-col sm:flex-row justify-between items-stretch sm:items-center gap-2">
                    <h4 className="text-xs font-black text-slate-400 uppercase tracking-widest">Enrolled Students</h4>
                    <div className="relative w-full sm:w-64">
                      <span className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none text-slate-400">
                        <FaSearch className="w-3 h-3" />
                      </span>
                      <input
                        type="text"
                        placeholder="Filter list..."
                        value={modalStudentSearch}
                        onChange={(e) => setModalStudentSearch(e.target.value)}
                        className="w-full pl-8 pr-3 py-1.5 text-xs bg-white border border-slate-200 rounded-lg placeholder:text-slate-400 text-slate-705 focus:outline-none focus:ring-1 focus:ring-slate-400 font-semibold"
                      />
                    </div>
                  </div>

                  {(() => {
                    const displayStudents = (selectedGroup.students || []).filter(std => {
                      if (!modalStudentSearch) return true;
                      const term = modalStudentSearch.toLowerCase();
                      const name = (std.name || '').toLowerCase();
                      const phone = (std.phoneNumber || '').toLowerCase();
                      return name.includes(term) || phone.includes(term);
                    });

                    return displayStudents.length > 0 ? (
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        {displayStudents.map((std) => {
                          const initials = std?.name ? std.name.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase() : 'ST';
                          const avatarStyle = getAvatarBgColor(std?.name);
                          return (
                            <div key={std?.id} className="flex justify-between items-center bg-white border border-slate-100 p-4 rounded-2xl transition-all hover:shadow-xs">
                              <div className="flex items-center gap-4">
                                <div className={`w-11 h-11 rounded-full flex items-center justify-center text-sm font-extrabold border flex-shrink-0 shadow-3xs ${avatarStyle}`}>
                                  {initials}
                                </div>
                                <div>
                                  <p className="font-extrabold text-slate-800 text-base leading-tight">{std?.name}</p>
                                  <p className="text-xs text-slate-400 font-semibold mt-1.5">{std?.phoneNumber || 'No phone listed'}</p>
                                </div>
                              </div>
                              <button
                                onClick={() => handleRemoveMember(std)}
                                className="text-slate-300 hover:text-rose-600 hover:bg-rose-50 p-2.5 rounded-xl transition-all cursor-pointer"
                                title="Remove Student"
                              >
                                <FaTrash className="w-4 h-4" />
                              </button>
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <p className="text-sm text-slate-405 italic font-semibold bg-white p-5 rounded-2xl text-center border border-slate-100 shadow-2xs">
                        {modalStudentSearch ? 'No matching students found.' : 'No students enrolled in this batch group.'}
                      </p>
                    );
                  })()}
                </div>
              </div>
            )}

            {/* Syllabus Progress Tab Content */}
            {activeViewTab === 'sessions' && (
              <div className="space-y-6 animate-fadeIn">
                <div className="bg-slate-100/70 p-4 rounded-xl border border-slate-200/30 text-sm text-slate-650 font-semibold flex justify-between items-center gap-4 flex-wrap">
                  <div>
                    Syllabus progress for <strong className="text-slate-800 font-extrabold">{selectedGroup.courseName}</strong>. 
                    {selectedGroup.instructors?.length > 1 && " You can assign different instructors to cover each session below."}
                  </div>
                  {selectedGroup.instructors?.length > 1 && (
                    <button
                      onClick={handleSaveAssignments}
                      className="bg-slate-800 hover:bg-slate-900 text-white font-bold text-xs uppercase tracking-wider px-4 py-2.5 rounded-lg shadow-2xs transition-all cursor-pointer border border-slate-700"
                    >
                      Save Assignments
                    </button>
                  )}
                </div>

                <div className="space-y-4 max-h-[50vh] overflow-y-auto pr-1 scrollbar-thin">
                  {sessionsList && sessionsList.length > 0 ? (
                    sessionsList.map((session, index) => {
                      const isAssignedToMe = instructorId ? (Number(session.instructorId) === Number(instructorId)) : true;
                      const hasActions = !instructorId || isAssignedToMe;
                      
                      // Status styling
                      let statusBg = "bg-slate-50 text-slate-500 border-slate-200/60";
                      let statusIcon = <FaClock className="text-slate-405" />;
                      if (session.status === 'ongoing') {
                        statusBg = "bg-blue-50 text-blue-705 text-blue-700 border-blue-200/50";
                        statusIcon = <FaPlay className="text-blue-500 w-3 h-3 animate-pulse" />;
                      } else if (session.status === 'completed') {
                        statusBg = "bg-emerald-50 text-emerald-705 text-emerald-700 border-emerald-200/50";
                        statusIcon = <FaCheckCircle className="text-emerald-500" />;
                      } else if (session.status === 'skipped') {
                        statusBg = "bg-rose-50 text-rose-705 text-rose-700 border-rose-200/50";
                        statusIcon = <FaBan className="text-rose-500" />;
                      }

                      return (
                        <div key={session.courseDetailId} className="bg-white border border-slate-200 p-5 rounded-2xl flex flex-col md:flex-row justify-between items-start md:items-center gap-4 transition-all hover:shadow-xs">
                          {/* Left: Info */}
                          <div className="flex-1 space-y-1.5 min-w-0">
                            <div className="flex flex-wrap items-center gap-2">
                              <span className="text-[11px] font-black uppercase tracking-wider bg-slate-100 text-slate-500 px-2 py-0.5 rounded-md border border-slate-200/50">
                                Session {index + 1}
                              </span>
                              <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-extrabold uppercase tracking-wide border ${statusBg}`}>
                                {statusIcon}
                                <span>{session.status}</span>
                              </span>
                            </div>
                            
                            <h4 className="text-base font-extrabold text-slate-800 leading-snug">
                              {session.mainTitle || "Topic Title"}
                            </h4>
                            <p className="text-xs text-slate-400 font-bold uppercase tracking-wider">
                              Section: {session.sectionName || "Section Name"}
                            </p>
                            {session.sectionDescription && (
                              <p className="text-xs text-slate-500 mt-1 leading-relaxed">
                                {session.sectionDescription}
                              </p>
                            )}

                            {/* Skipped Reason Alert */}
                            {session.status === 'skipped' && session.skipReason && (
                              <div className="mt-2.5 p-3 rounded-xl bg-rose-50/40 border border-rose-100/50 text-xs text-rose-750 text-rose-700 flex items-start gap-2">
                                <FaExclamationTriangle className="mt-0.5 text-rose-500 flex-shrink-0" />
                                <div>
                                  <span className="font-bold">Reason for Skip:</span> {session.skipReason}
                                </div>
                              </div>
                            )}

                            {/* Instructor Info label / Selection */}
                            <div className="mt-3 pt-2.5 border-t border-slate-100 flex flex-wrap items-center gap-3">
                              {selectedGroup.instructors?.length <= 1 ? (
                                <span className="text-xs text-slate-400 font-semibold flex items-center gap-2">
                                  {(() => {
                                    const instructorName = session.instructorName || selectedGroup.instructors?.[0]?.name || "Primary Instructor";
                                    const initials = instructorName.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase();
                                    const avatarStyle = getAvatarBgColor(instructorName);
                                    return (
                                      <>
                                        <span className={`w-5 h-5 rounded-full flex items-center justify-center text-[9px] font-extrabold border ${avatarStyle}`}>
                                          {initials}
                                        </span>
                                        <span>
                                          Covered by: <strong className="text-slate-600 font-extrabold">{instructorName}</strong>
                                        </span>
                                      </>
                                    );
                                  })()}
                                </span>
                              ) : (
                                <>
                                  {instructorId ? (
                                    /* Instructor Dashboard view: show checkbox to claim */
                                    <label className="inline-flex items-center gap-2 cursor-pointer select-none text-xs font-bold text-slate-600">
                                      <input
                                        type="checkbox"
                                        checked={Number(session.instructorId) === Number(instructorId)}
                                        onChange={(e) => {
                                          const claimed = e.target.checked;
                                          handleSessionInstructorChange(session.courseDetailId, claimed ? instructorId : null);
                                        }}
                                        className="rounded border-slate-300 text-slate-800 focus:ring-slate-800 w-4 h-4 cursor-pointer"
                                      />
                                      <span>I will cover this session</span>
                                      {session.instructorId && Number(session.instructorId) !== Number(instructorId) && (
                                        <span className="text-slate-400 font-medium italic">
                                          (Currently assigned to {session.instructorName || "another"})
                                        </span>
                                      )}
                                    </label>
                                  ) : (
                                    /* Admin Dashboard view: show select dropdown */
                                    <div className="flex items-center gap-2">
                                      {session.instructorId && (
                                        <span className={`w-5 h-5 rounded-full flex items-center justify-center text-[9px] font-extrabold border ${getAvatarBgColor(session.instructorName)}`}>
                                          {session.instructorName ? session.instructorName.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase() : 'IN'}
                                        </span>
                                      )}
                                      <span className="text-xs text-slate-400 font-bold uppercase tracking-wider">Assign Instructor:</span>
                                      <select
                                        value={session.instructorId || ''}
                                        onChange={(e) => handleSessionInstructorChange(session.courseDetailId, e.target.value)}
                                        className="bg-slate-50 hover:bg-slate-100 border border-slate-200 px-2.5 py-1.5 rounded-lg text-xs text-slate-700 font-bold focus:outline-none focus:ring-1 focus:ring-slate-800 transition-all cursor-pointer"
                                      >
                                        <option value="">-- Unassigned --</option>
                                        {selectedGroup.instructors?.map(inst => (
                                          <option key={inst.id} value={inst.id}>{inst.name}</option>
                                        ))}
                                      </select>
                                    </div>
                                  )}
                                </>
                              )}
                            </div>
                          </div>

                          {/* Right: Actions */}
                          <div className="shrink-0 flex gap-2">
                            {session.status === 'pending' && (
                              <button
                                onClick={() => handleUpdateSessionStatusClick(session, 'ongoing')}
                                disabled={!hasActions}
                                className="bg-slate-800 hover:bg-slate-900 text-white font-bold text-xs uppercase tracking-wider px-3.5 py-2.5 rounded-lg shadow-3xs hover:shadow-2xs transition-all disabled:opacity-40 cursor-pointer flex items-center gap-1.5 animate-hover-lift"
                                title={!hasActions ? "Only the assigned instructor can start this session" : "Start Session"}
                              >
                                <FaPlay className="w-2 h-2 text-white" /> Start
                              </button>
                            )}

                            {session.status === 'ongoing' && (
                              <button
                                onClick={() => handleUpdateSessionStatusClick(session, 'completed')}
                                disabled={!hasActions}
                                className="bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-xs uppercase tracking-wider px-3.5 py-2.5 rounded-lg shadow-3xs hover:shadow-2xs transition-all disabled:opacity-40 cursor-pointer flex items-center gap-1.5 animate-hover-lift"
                                title={!hasActions ? "Only the assigned instructor can complete this session" : "Complete Session"}
                              >
                                <FaCheckCircle className="w-3.5 h-3.5 text-white" /> Complete
                              </button>
                            )}

                            {(session.status === 'completed' || session.status === 'skipped') && (
                              <span className="text-xs text-slate-400 font-bold uppercase tracking-wider italic p-2.5 bg-slate-50 rounded-lg border border-slate-100">
                                Done
                              </span>
                            )}
                          </div>
                        </div>
                      );
                    })
                  ) : (
                    <div className="text-center py-16 bg-white border border-slate-100 rounded-2xl shadow-3xs text-slate-400 font-semibold italic">
                      No sessions or sections are defined for this course syllabus.
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}
      </Modal>

      {/* Skip Reason Prompt Modal */}
      <Modal isOpen={skipModalOpen} onClose={() => setSkipModalOpen(false)} size="md">
        {targetSession && (
          <form onSubmit={handleSkipModalSubmit} className="p-6 space-y-5 bg-white rounded-xl">
            <div className="border-b border-slate-100 pb-4">
              <h3 className="text-lg font-extrabold text-slate-800 flex items-center gap-2 text-rose-600">
                <FaExclamationTriangle className="w-5 h-5 text-rose-500" /> Skip Previous Sessions?
              </h3>
              <p className="text-xs text-slate-400 mt-1 font-medium">
                You are skipping preceding sessions to start/complete <strong className="text-slate-700 font-extrabold">"{targetSession.mainTitle}"</strong>. Please provide a reason for skipping each of them.
              </p>
            </div>

            <div className="space-y-4 max-h-[45vh] overflow-y-auto pr-1 scrollbar-thin">
              {skippedSessions.map((s, idx) => (
                <div key={s.courseDetailId} className="bg-slate-50 p-4 rounded-xl border border-slate-200/50 space-y-2.5">
                  <div className="flex flex-col gap-0.5 text-xs font-bold">
                    <span className="text-slate-500 uppercase tracking-wider">
                      Session {sessionsList.findIndex(x => x.courseDetailId === s.courseDetailId) + 1}
                    </span>
                    <span className="text-slate-800 font-extrabold text-sm truncate" title={s.mainTitle}>
                      {s.mainTitle}
                    </span>
                  </div>
                  <div>
                    <label className="block text-[10px] font-black text-slate-400 uppercase tracking-wider mb-1.5">
                      Skip Reason <span className="text-rose-500">*</span>
                    </label>
                    <textarea
                      required
                      placeholder="Explain why this session is being skipped (e.g., covered in previous batch, self-study, or reschedule)..."
                      value={skipReasons[s.courseDetailId] || ''}
                      onChange={(e) => {
                        const val = e.target.value;
                        setSkipReasons(prev => ({
                          ...prev,
                          [s.courseDetailId]: val
                        }));
                      }}
                      rows="2"
                      className="w-full bg-white border border-slate-200 rounded-lg px-3 py-2 text-xs font-medium text-slate-700 focus:outline-none focus:ring-1 focus:ring-slate-800 focus:border-slate-350 transition-all resize-none shadow-3xs"
                    />
                  </div>
                </div>
              ))}
            </div>

            {/* Action Buttons */}
            <div className="flex gap-3 pt-4 border-t border-slate-100">
              <button
                type="submit"
                className="flex-1 py-2.5 rounded-lg text-sm font-bold bg-slate-800 hover:bg-slate-900 text-white shadow-2xs transition-all duration-200 cursor-pointer"
              >
                Submit & Proceed
              </button>
              <button
                type="button"
                onClick={() => setSkipModalOpen(false)}
                className="flex-1 py-2.5 rounded-lg text-sm font-bold bg-slate-100 hover:bg-slate-200 text-slate-600 shadow-3xs transition-all duration-200 cursor-pointer"
              >
                Cancel
              </button>
            </div>
          </form>
        )}
      </Modal>

      {/* Dedicated Interactive Batch Edit Modal */}
      <Modal isOpen={editModal} onClose={() => { setEditModal(false); setEditingGroup(null); }} size="md">
        {editingGroup && (
          <form onSubmit={handleEditSubmit} className="p-6 space-y-5 bg-white rounded-xl">
            <div className="border-b border-slate-100 pb-4">
              <h3 className="text-lg font-extrabold text-slate-800 flex items-center gap-2.5">
                <FaEdit className="text-slate-700 w-5 h-5" /> Edit Batch Settings
              </h3>
              <p className="text-xs text-slate-400 mt-1 font-medium">Modify dates, time slots, or assign the primary instructor.</p>
            </div>

            <div className="space-y-4">
              {/* Read only info badges */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <span className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-1.5">Course</span>
                  <div className="bg-slate-50 border border-slate-100 rounded-lg px-3 py-2 text-sm text-slate-600 font-bold truncate" title={editingGroup.courseName}>
                    {editingGroup.courseName}
                  </div>
                </div>
                <div>
                  <span className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-1.5">Batch</span>
                  <div className="bg-slate-50 border border-slate-100 rounded-lg px-3 py-2 text-sm text-slate-600 font-bold truncate">
                    {editingGroup.batchName}
                  </div>
                </div>
              </div>

              {/* Primary Instructor Selector */}
              <div>
                <label className="block text-xs font-bold text-slate-505 uppercase tracking-wider mb-1.5">Primary Instructor</label>
                <select
                  value={editInstructor}
                  onChange={(e) => { setEditInstructor(e.target.value); setEditConflictError(false); }}
                  disabled={!!instructorId}
                  className={`w-full bg-white border rounded-lg px-3 py-2.5 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-slate-200 transition-all ${editConflictError ? 'border-red-400 bg-red-50/20 focus:ring-red-200' : 'border-slate-200'
                    }`}
                  required
                >
                  <option value="">-- Select Instructor --</option>
                  {allInstructors.map((inst) => (
                    <option key={inst.id} value={inst.id}>
                      {inst.name} {inst.email ? `(${inst.email})` : ''}
                    </option>
                  ))}
                </select>
              </div>

              {/* Start and End Dates */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold text-slate-505 uppercase tracking-wider mb-1.5">Start Date</label>
                  <input
                    type="date"
                    value={editStartDate}
                    onChange={(e) => setEditStartDate(e.target.value)}
                    className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-slate-200"
                    required
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-505 uppercase tracking-wider mb-1.5">End Date</label>
                  <input
                    type="date"
                    value={editEndDate}
                    onChange={(e) => setEditEndDate(e.target.value)}
                    className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-slate-200"
                    required
                  />
                </div>
              </div>

              {/* Start and End Times */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold text-slate-505 uppercase tracking-wider mb-1.5">Start Time</label>
                  <input
                    type="time"
                    value={editStartTime}
                    onChange={(e) => setEditStartTime(e.target.value)}
                    className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-slate-200"
                    required
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-505 uppercase tracking-wider mb-1.5">End Time</label>
                  <input
                    type="time"
                    value={editEndTime}
                    onChange={(e) => setEditEndTime(e.target.value)}
                    className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-slate-200"
                    required
                  />
                </div>
              </div>

              {/* Frequency Toggle Switch */}
              <div className="bg-slate-50/50 p-4 rounded-xl border border-slate-100 flex items-center justify-between">
                <div>
                  <h4 className="text-sm font-bold text-slate-700 leading-none">Class Frequency</h4>
                  <p className="text-xs text-slate-400 mt-1.5 font-medium">Toggle between Daily lectures or Weekend schedule.</p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={editIsWeekend}
                    onChange={(e) => setEditIsWeekend(e.target.checked)}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-slate-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[3px] after:left-[3px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-slate-800"></div>
                  <span className="ml-3 text-sm font-bold text-slate-700 w-16">
                    {editIsWeekend ? 'Weekend' : 'Daily'}
                  </span>
                </label>
              </div>

              {/* Feedback Alerts */}
              {editMessage && (
                <div
                  className={`p-3.5 rounded-lg text-xs border flex gap-2.5 items-start ${editConflictError
                    ? 'bg-rose-50 border-rose-100 text-rose-800'
                    : editMessage.toLowerCase().includes('success')
                      ? 'bg-emerald-50 border-emerald-100 text-emerald-800'
                      : 'bg-indigo-50 border-indigo-100 text-indigo-800'
                    }`}
                >
                  <FaInfoCircle className="mt-0.5 flex-shrink-0 text-sm" />
                  <p className="leading-normal font-semibold">{editMessage}</p>
                </div>
              )}
            </div>

            {/* Action Buttons */}
            <div className="flex gap-3 pt-4 border-t border-slate-100">
              <button
                type="submit"
                disabled={editLoading}
                className="flex-1 py-2.5 rounded-lg text-sm font-bold bg-slate-800 hover:bg-slate-900 text-white shadow-2xs transition-all duration-200 cursor-pointer disabled:bg-slate-200 animate-hover-lift"
              >
                {editLoading ? 'Saving...' : 'Save Changes'}
              </button>
              <button
                type="button"
                onClick={() => { setEditModal(false); setEditingGroup(null); }}
                disabled={editLoading}
                className="flex-1 py-2.5 rounded-lg text-sm font-bold bg-slate-100 hover:bg-slate-200 text-slate-700 shadow-3xs transition-all duration-200 cursor-pointer"
              >
                Cancel
              </button>
            </div>
          </form>
        )}
      </Modal>
    </div>
  );
};

export default GroupBatchList;